import {useMapConfig} from '@/hooks/useAppConfig'
import {YYZ_T1_PICKUP} from '@/features/navigation/data/yyzArrivalEntries'

/**
 * THE FAINT AIRPORT BEHIND THE WALK — decoration, and honest about it.
 *
 * ── ⚠️ IT IS NOT A MAP OF THIS TRIP AND MUST NOT BE READ AS ONE ───────────────────
 * It is always Terminal 1's curb at a fixed zoom, even on a Terminal 3 ride, exactly as
 * the mobile app does it. Nothing on it is positioned: the walk drawn on top has no
 * relationship to the roads underneath. Its whole job is to stop the stage being a white
 * void, so that the mint line reads as a route through somewhere rather than as a graph.
 *
 * Which is also why it is so faint. Anything more legible would invite a passenger to look
 * for themselves on it, and they are not there.
 *
 * ── ⚠️ A STATIC IMAGE, NOT A GL MAP ───────────────────────────────────────────────
 * A second live Mapbox instance inside a sheet that already has one costs a WebGL context,
 * a tile stream and a canvas — for a picture that never moves and cannot be touched. The
 * Static Images API is one request for the same pixels.
 *
 * ── ⚠️ AND IT DISAPPEARS RATHER THAN FAILING ──────────────────────────────────────
 * With no Mapbox token — the config request pending, or an environment without one — this
 * renders nothing at all. A broken-image frame behind the instructions would be worse than
 * the plain background it is decorating.
 */

/** The mobile screen's own framing: T1's curb, close enough to show the terminal loops. */
const ZOOM = 15.5
const STYLE = 'light-v11'

export default function SilverMapBackground({className = ''}) {
    const {token} = useMapConfig()
    if (!token) return null

    const [lng, lat] = YYZ_T1_PICKUP
    /* @2x for a retina panel; the size is generous because the stage grows with the sheet
       and a stretched raster is more obvious than a slightly oversized one. */
    const src = `https://api.mapbox.com/styles/v1/mapbox/${STYLE}/static/${lng},${lat},${ZOOM},0/600x600@2x?access_token=${token}&logo=false&attribution=false`

    return (
        /* ⚠️ `aria-hidden` AND NO ALT. It carries no information — describing it to a
           screen reader would be reading out a decoration as if it were the route. */
        <div className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
            <img
                src={src}
                alt=""
                /* Washed out in two steps: grayscale takes the colour out, the low opacity
                   takes the contrast down. Either alone still competes with the line. */
                className="h-full w-full object-cover opacity-[0.18] grayscale"
                loading="lazy"
            />
        </div>
    )
}

export {SilverMapBackground}
